
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MESSAGES } from "@/constants/testIds";
import { toast } from "sonner";
import { Send, Lock } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80";

export default function Messages() {
    const { user } = useAuth();
    const [sp] = useSearchParams();
    const initialTo = sp.get("to");
    const [conversations, setConversations] = useState([]);
    const [active, setActive] = useState(initialTo);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const scrollerRef = useRef(null);

    const loadConvs = async () => {
        try {
            const { data } = await client.get("/conversations");
            setConversations(data);
            if (!active && data.length) setActive(data[0].other?.user_id);
        } catch { /* ignore - empty list will render */ }
        finally { setLoading(false); }
    };

    const loadMessages = async (otherId) => {
        if (!otherId) return;
        try {
            const { data } = await client.get(`/messages/${otherId}`);
            setMessages(data);
            setTimeout(() => scrollerRef.current?.scrollTo(0, scrollerRef.current.scrollHeight), 50);
        } catch { /* no messages */ }
    };

    useEffect(() => { loadConvs(); }, []);
    useEffect(() => { loadMessages(active); }, [active]);

    // If a `to` param exists and we don't have a conversation with them, allow starting one
    useEffect(() => {
        if (initialTo && !conversations.find((c) => c.other?.user_id === initialTo)) {
            client.get(`/profiles/${initialTo}`).then(({ data }) => {
                setConversations((prev) => [{ conversation_id: "new", participants: [user?.user_id, initialTo], other: data, last_text: "" }, ...prev]);
            }).catch(() => { /* profile not found */ });
        }
    }, [initialTo]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !active) return;
        try {
            await client.post("/messages", { recipient_id: active, text: text.trim() });
            setText("");
            await loadMessages(active);
            await loadConvs();
        } catch (err) {
            if (err.response?.status === 402) {
                toast.error("Upgrade to Premium for unlimited messaging");
            } else {
                toast.error(err.response?.data?.detail || "Send failed");
            }
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">Messages</div>
            <h1 className="mt-2 font-serif text-5xl text-navy tracking-tighter">Conversations</h1>

            <div className="mt-8 grid lg:grid-cols-12 gap-6 bg-white border border-navy-100 rounded-2xl overflow-hidden" style={{ minHeight: 560 }}>
                {/* Convs list */}
                <div className="lg:col-span-4 border-r border-navy-100 overflow-y-auto" style={{ maxHeight: 560 }}>
                    {loading ? <div className="p-6 text-navy-400">Loading…</div> :
                        conversations.length === 0 ? <div className="p-6 text-navy-400 text-sm font-sans">No conversations yet.</div> :
                        conversations.map((c) => {
                            const o = c.other; if (!o) return null;
                            const photo = o.photos?.[0];
                            const src = photo ? (photo.startsWith("http") ? photo : `${process.env.REACT_APP_BACKEND_URL}${photo}`) : FALLBACK;
                            return (
                                <button key={o.user_id} onClick={() => setActive(o.user_id)} data-testid={MESSAGES.conversation}
                                    className={`w-full flex items-center gap-3 p-4 border-b border-navy-50 hover:bg-linen transition-colors text-left ${active === o.user_id ? "bg-linen" : ""}`}>
                                    <img src={src} alt="" className="h-12 w-12 rounded-full object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-sans font-semibold text-navy text-sm truncate">{o.full_name || o.name}</div>
                                        <div className="text-xs text-navy-400 font-sans truncate">{c.last_text || "Start a conversation"}</div>
                                    </div>
                                </button>
                            );
                        })}
                </div>

                {/* Chat */}
                <div className="lg:col-span-8 flex flex-col" style={{ maxHeight: 560 }}>
                    {!active ? (
                        <div className="flex-1 flex items-center justify-center text-navy-400 font-sans">Select a conversation</div>
                    ) : (
                        <>
                            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-6 space-y-3">
                                {messages.length === 0 ? (
                                    <div className="text-center text-sm text-navy-400 font-sans py-10">No messages yet. Say hello.</div>
                                ) : messages.map((m) => {
                                    const mine = m.sender_id === user?.user_id;
                                    return (
                                        <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 font-sans text-sm leading-relaxed ${mine ? "bg-navy text-white rounded-br-sm" : "bg-linen text-navy rounded-bl-sm"}`}>
                                                {m.text}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={send} className="border-t border-navy-100 p-3 flex gap-2">
                                <input value={text} onChange={(e) => setText(e.target.value)}
                                    data-testid={MESSAGES.input}
                                    placeholder="Write with reverence..."
                                    className="flex-1 bg-linen border border-navy-100 rounded-lg px-5 py-3 focus:ring-2 focus:ring-gold focus:border-transparent outline-none font-sans text-sm" />
                                <button type="submit" data-testid={MESSAGES.send}
                                    className="bg-gold text-navy hover:bg-gold-600 hover:text-white rounded-lg px-5 transition-all active:scale-95 shadow-md shadow-gold/20 flex items-center gap-2">
                                    <Send className="h-4 w-4" />
                                </button>
                            </form>
                            {!user?.premium_until && (
                                <div className="px-4 pb-3 text-[10px] uppercase tracking-wider text-navy-400 flex items-center gap-1 font-sans">
                                    <Lock className="h-3 w-3" /> Free plan: limited to 3 messages. <a href="/premium" className="text-gold-700 underline ml-1">Go Premium</a>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
